import { Component, OnInit, OnDestroy, Output, EventEmitter, ViewChild, ElementRef } from '@angular/core';
import { FileUploadService } from '../../services/file-upload.service';
import { map, catchError } from 'rxjs/operators';
import { HttpEventType, HttpErrorResponse } from '@angular/common/http';
import { of, Subscription } from 'rxjs';
import { FileResponse } from '../../models/FileUploadResponse';
import { SharedSnackBarHandlerService } from '../../services/shared.snackbar.handler.service';
import { ApiResponse } from '../../models/ApiResponse';

/*
  Dedicated, Excel-only sibling of velocity-file-uploader (same upload mechanics), so that other
  consumers of velocity-file-uploader aren't put at risk by changes made for spreadsheet-only uploads.

  This component can support multiple file upload, but currently it is modified to have only one file upload.
  in future if required we can modify to have multiple file upload. Currently, we are taking only first file.
 */

const ACCEPTED_FILE_TYPES = ['.xls', '.xlsx', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];

@Component({
  selector: 'velocity-sheet-uploader',
  templateUrl: './sheet-uploader.component.html',
  styleUrls: ['./sheet-uploader.component.scss'],
})
export class SheetUploaderComponent implements OnInit, OnDestroy {
  public readonly acceptedFileTypes = ACCEPTED_FILE_TYPES.join(',');
  @Output()
  public uploadedFile = new EventEmitter<FileResponse>();
  public IsFileOver: boolean;
  public currentUploadedFile = new ApiResponse<FileResponse>();
  public isFileLoading: boolean;
  private subs: Subscription[] = [];
  @ViewChild("fileUpload", { static: false }) fileUpload: ElementRef; files = [];

  constructor(
    private fileUploadService: FileUploadService,
    private snackBarHandlerService: SharedSnackBarHandlerService,

  ) {

  }

  ngOnInit(): void {
    this.isFileLoading = false;
    this.subs = [];
  }

  ngOnDestroy(): void {
    this.subs.forEach((subscription) => {
      if (subscription)
        subscription.unsubscribe()
    })
  }

  uploadFile(file) {
    const formData = new FormData();
    formData.append('file', file.data);
    file.inProgress = true;
    this.isFileLoading = true;
    this.fileUploadService.upload(formData).pipe(
      map(event => {
        let progress = 0;
        switch (event.type) {
          case HttpEventType.UploadProgress:
            progress = Math.round(event.loaded * 100 / event.total);
            if (progress === 100) {
              progress = 99;
            }
            file.progress = progress;

            break;
          case HttpEventType.Response:

            return event;
        }
      }),
      catchError((error: HttpErrorResponse) => {
        file.inProgress = false;
        this.snackBarHandlerService.showError(`${file.data.name} upload failed.`);
        return of(`${file.data.name} upload failed.`);
      })).subscribe((event: any) => {
        file.inProgress = false;
        if (typeof (event) === 'object') {
          file.progress = 100;
          this.currentUploadedFile = event.body;
          if (this.currentUploadedFile.isSuccess) {
            this.uploadedFile.emit(this.currentUploadedFile.data);
            this.files = [];
            this.currentUploadedFile = new ApiResponse<FileResponse>();
          } else {
            this.snackBarHandlerService.showError(this.currentUploadedFile.responseMessage);
            this.files = [];
            this.currentUploadedFile.data = new FileResponse();
            this.uploadedFile.emit(this.currentUploadedFile.data);
          }
        }
        this.isFileLoading = false;
      })
      ;
  }

  onClick() {
    const fileUpload = this.fileUpload.nativeElement;
    fileUpload.onchange = () => {
      this.files = [];
      for (let index = 0; index < fileUpload.files.length; index++) {
        const file = fileUpload.files[index];
        if (this.acceptFile(file)) {
          this.files.push({ data: file, inProgress: false, progress: 0 });
        }
      }
      if (this.files.length > 0) {
        this.uploadFiles();
      }
    };
    fileUpload.click();
  }

  onFileDropped($event) {
    for (const item of $event) {
      if (this.acceptFile(item)) {
        this.files.push({ data: item, inProgress: false, progress: 0 });
      }
    }
    if (this.files.length > 0) {
      this.uploadFiles();
    }
  }

  private acceptFile(file: File): boolean {
    const fileName = (file.name || '').toLowerCase();
    const fileType = (file.type || '').toLowerCase();

    const isAccepted = ACCEPTED_FILE_TYPES.some(type => {
      if (type.startsWith('.')) {
        return fileName.endsWith(type);
      }

      return fileType === type;
    });

    if (!isAccepted) {
      this.snackBarHandlerService.showError(`${file.name} is not an Excel file.`);
    }

    return isAccepted;
  }

  private uploadFiles() {
    this.fileUpload.nativeElement.value = '';
    this.files.forEach(file => {
      this.uploadFile(file);
    });
  }

  OnRemoveFileClick() {
    if (this.currentUploadedFile.data === null || typeof this.currentUploadedFile.data === 'undefined') {
      return;
    }
    this.isFileLoading = true;
    this.subs.push(
      this.fileUploadService.deleteFile(this.currentUploadedFile.data).subscribe(response => {
        if (!response) {
          this.snackBarHandlerService.showError("Error in deleting file");
          return;
        }
        if (this.currentUploadedFile.isSuccess) {
          this.snackBarHandlerService.showMessage("Success", "File Deleted");
          this.files = [];
          this.currentUploadedFile = new ApiResponse<FileResponse>();
          this.uploadedFile.emit(this.currentUploadedFile.data);
        }
        else {
          this.snackBarHandlerService.showError(response.responseMessage);
        }
        this.isFileLoading = false;

      })
    );
  }
  OnDragOver(isOver: boolean) {
    this.IsFileOver = isOver;
  }

  public clearFiles() {
    this.files = [];
    this.currentUploadedFile = new ApiResponse<FileResponse>();
  }
}
